import React, { useEffect, useRef, useState, useCallback } from "react";
import styles from "./OnSaleBanner.module.css";
import WebService from "../../Services/WebService";
import { primaryProductImage } from "../../Services/productImages";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

interface OnSaleItem {
  product_id: number;
  category_id: number;
  product_type: string;         
  product_name: string;
  image?: string;
  images?: { file_name: string }[];
  image_document_id?: number;
  discount?: number;             
  is_most_selling?: boolean;
  trending_rank?: number | null;
}

type Pagination = {
  current_page: number;
  page_size: number;
  total_records: number;
  total_pages: number;
};

type ApiResp = {
  status_code?: number;
  message?: string;
  data?: {
    pagination?: Pagination;
    products?: any[];
  };
};

const PAGE_SIZE = 10; 
const CARD_SCROLL_FALLBACK = 320;

const OnSaleBanner: React.FC = () => {
  const [items, setItems] = useState<OnSaleItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(false);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const mapProducts = (res: ApiResp): OnSaleItem[] => {
    const raw = res?.data?.products ?? [];
    if (!Array.isArray(raw)) return [];

    return raw.map((p: any): OnSaleItem => ({
      product_id: Number(p.product_id ?? p.id),
      category_id: Number(p.category_id),
      product_type: (p.product_type || p.type_category || "PRODUCT").toString().toUpperCase(),
      product_name: p.product_name || p.name || "",
      image: p.image,
      image_document_id: p.image_document_id,
      images: p.images,
      is_most_selling: p.is_most_selling === true,
      trending_rank: typeof p.trending_rank === "number" ? p.trending_rank : null,
      // Sale discounts only apply to products that are also marked "Available for Sale"
      discount:
        p.is_on_sale !== true
          ? 0
          : typeof p.discount === "number"
          ? p.discount
          : typeof p.customer_sale_discount_per === "number"
          ? p.customer_sale_discount_per
          : typeof p.distributor_sale_discount_per === "number"
          ? p.distributor_sale_discount_per
          : 0,
    }));
  };

  const updateHasMore = (res: ApiResp) => {
    const pg = res?.data?.pagination;
    if (pg && typeof pg.current_page === "number" && typeof pg.total_pages === "number") {
      setHasMore(pg.current_page < pg.total_pages);
    } else {
      // No pagination meta => assume single batch
      setHasMore(false);
    }
  };

  const refreshArrowState = () => {
    const el = scrollRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const left = el.scrollLeft;
    const maxLeft = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(left > 0);
    setCanScrollRight(left < maxLeft - 2);
  };

  const fetchPage = useCallback(
    async (nextPage: number, append = true) => {
      if (nextPage === 1) {
        setInitialLoading(true);
      } else {
        if (!hasMore || fetchingMore) return;
        setFetchingMore(true);
      }

      try {
        const res = await WebService.getAPI<ApiResp>({
          action: `/v1/door-management/dashboard/trending?page_number=${nextPage}&limit=${PAGE_SIZE}`,
        });

        const batch = mapProducts(res);
        setItems(prev => (append ? (nextPage === 1 ? batch : [...prev, ...batch]) : batch));
        setPage(nextPage);
        updateHasMore(res);
      } catch {
        if (nextPage === 1) {
          setItems([]);
          setHasMore(false);
        } else {
          setHasMore(false);
        }
      } finally {
        setInitialLoading(false);
        setFetchingMore(false);
        setTimeout(refreshArrowState, 0);
      }
    },
    [fetchingMore, hasMore]
  );

  useEffect(() => {
    fetchPage(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recompute arrows once cards are in the DOM (the list mounts only after the first fetch)
  useEffect(() => {
    refreshArrowState();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => refreshArrowState());
    ro.observe(el);
    return () => ro.disconnect();
  }, [items.length]);

  // Recompute arrows on resize
  useEffect(() => {
    const onResize = () => refreshArrowState();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleScroll = () => {
    refreshArrowState();

    const el = scrollRef.current;
    if (!el || initialLoading || fetchingMore || !hasMore) return;
    const nearEnd =
      el.scrollLeft + el.clientWidth >= el.scrollWidth - Math.max(300, el.clientWidth / 2);
    if (nearEnd) fetchPage(page + 1, true);
  };

  const scrollByCards = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;

    const card = el.querySelector(`.${styles.saleCard}`) as HTMLElement | null;
    const step = card ? card.offsetWidth + 38 : CARD_SCROLL_FALLBACK;
    const delta = (dir === "left" ? -1 : 1) * step * 2;

    el.scrollBy({ left: delta, behavior: "smooth" });

    if (dir === "right") {
      window.requestAnimationFrame(() => {
        setTimeout(() => {
          refreshArrowState();
          const nearEnd =
            el.scrollLeft + el.clientWidth >= el.scrollWidth - Math.max(300, el.clientWidth / 2);
          if (nearEnd && hasMore && !fetchingMore && !initialLoading) {
            fetchPage(page + 1, true);
          }
        }, 180);
      });
    }
  };

  // === Fallback UI when there are no trending items ===
  if (!initialLoading && items.length === 0) {
    return (
      <div className={styles.onSaleWrapper}>
        <div className={styles.headerRow}>
          <div className={styles.onSaleLabel}>
            <span className={styles.saleFire}>🔥</span>
            <span className={styles.storiesTitle}>Trending</span>
            <span className={styles.saleFire}>🔥</span>
          </div>
        </div>

        <div
          style={{
            background: "#fff8d9",
            border: "1px solid #ffe48c",
            borderRadius: 12,
            padding: "14px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ fontWeight: 700, color: "#7b5b00" }}>
            Trending products will appear here soon! 💛
          </div>
          {/* <button
            type="button"
            onClick={() => navigate("/shop")}
            style={{
              background: "#FFD400",
              color: "#1a1a1a",
              fontWeight: 800,
              border: "none",
              borderRadius: 8,
              padding: "8px 14px",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,.06)",
            }}
          >
            Browse Products
          </button> */}
        </div>
      </div>
    );
  }

  // When loading the very first page, keep layout clean (no jitter)
  if (initialLoading && items.length === 0) return null;

  return (
    <div className={styles.onSaleWrapper}>
      <div className={styles.headerRow}>
        <div className={styles.onSaleLabel}>
          <span className={styles.saleFire}>🔥</span>
          <span className={styles.storiesTitle}>Trending</span>
          <span className={styles.saleFire}>🔥</span>
        </div>
        <div className={styles.arrowBtns}>
          <button
            aria-label="Scroll left"
            className={styles.arrowBtn}
            onClick={() => scrollByCards("left")}
            type="button"
            disabled={!canScrollLeft}
          >
            <FaChevronLeft />
          </button>
          <button
            aria-label="Scroll right"
            className={styles.arrowBtn}
            onClick={() => scrollByCards("right")}
            type="button"
            disabled={!canScrollRight && !hasMore && !fetchingMore}
          >
            <FaChevronRight />
          </button>
        </div>
      </div>

      <div className={styles.saleListOuter}>
        <div className={styles.saleList} ref={scrollRef} onScroll={handleScroll}>
          {items.map((item, idx) => {
            const imgUrl = primaryProductImage(item);
            return (
              <div
                className={styles.saleCard}
                key={item.product_id + "_" + idx}
                tabIndex={0}
                title={item.product_name}
                onClick={() =>
                  navigate(`/watchlist/${item.product_type}/${item.category_id}/${item.product_id}`)
                }
              >
                <div className={styles.imgWrap}>
                  <img
                    src={imgUrl}
                    alt={item.product_name}
                    className={styles.cardImg}
                    draggable={false}
                    loading="lazy"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "/Assets/no-pictures.png";
                    }}
                  />
                  {typeof item.discount === "number" && item.discount > 0 && (
                    <span className={styles.discountTag}>-{item.discount}%</span>
                  )}
                  {item.trending_rank ? (
                    <span className={styles.mostSellingRibbon}>#{item.trending_rank} Trending</span>
                  ) : item.is_most_selling ? (
                    <span className={styles.mostSellingRibbon}>Trending</span>
                  ) : null}
                </div>
                <div className={styles.cardName}>{item.product_name}</div>
              </div>
            );
          })}

          {fetchingMore && hasMore && (
            <div
              style={{
                minWidth: 120,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Loading…
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnSaleBanner;
